import { ClinicDetail } from '@/types/admin-clinic-detail';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { useNavigate, useParams } from 'react-router-dom';
import { AppButton } from '@components/atoms';
import { DeleteFill, EditFill } from '@components/atoms/AppIcon';
import { StandardDeleteConfirmContent } from '@/components/molecules';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import { getSiteTypeLabel } from '@/shared/constants/siteTypes';

type ClinicBasicInfoProps = Omit<
  ClinicDetail,
  'clinic_informations' | 'massagers' | 'users' | 'clinic_locations'
> & {
  /** サイト区分 display name (same as create form, from tenants table) */
  siteTypeDisplayName?: string | null;
  onDelete: () => void;
  isDeleting: boolean;
};

const ClinicBasicInfo = ({
  id,
  clinic_number,
  site_type,
  service_type,
  available_from,
  available_to,
  is_excluded_transfer,
  siteTypeDisplayName,
  onDelete,
  isDeleting,
}: ClinicBasicInfoProps) => {
  const openStandardDeleteConfirm = useStandardDeleteConfirm();
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();

  const serviceType: Record<string, string> = {
    r2: 'マッサージ',
    a2: 'はり・きゅう',
    both: 'はり・きゅう、マッサージ',
  };

  return (
    <section className="my-4 space-y-2">
      <h3 className="text-xl">治療院詳細:{id}</h3>
      <div className="flex items-center justify-between">
        <h3 className="text-xl">治療院:{clinic_number}</h3>
        <div className="flex gap-2">
          <AppButton icon={<EditFill />} type="primary" onClick={() => navigate(`edit`)}>
            編集
          </AppButton>
          <AppButton
            icon={<DeleteFill />}
            danger
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              openStandardDeleteConfirm({
                content: <StandardDeleteConfirmContent />,
                okButtonProps: { loading: isDeleting },
                onOk: onDelete,
              });
            }}
          >
            削除
          </AppButton>
        </div>
      </div>
      <dl className="grid grid-cols-12 gap-y-2 text-sm">
        {(site === 'super-admin' || site === 'medix') && (
          <>
            <dt className="col-span-2 font-semibold">サイト区分</dt>
            <dd className="col-span-10">
              {siteTypeDisplayName ?? getSiteTypeLabel(site_type)}
            </dd>
          </>
        )}
        <dt className="col-span-2 font-semibold">治療院ID</dt>
        <dd className="col-span-10">{clinic_number}</dd>
        <dt className="col-span-2 font-semibold">有効期限</dt>
        <dd className="col-span-10">{formatPeriodDisplay(available_from, available_to)}</dd>
        {service_type && (
          <>
            <dt className="col-span-2 font-semibold">サービス</dt>
            <dd className="col-span-10">{serviceType[service_type.toLowerCase()]}</dd>
          </>
        )}
        <dt className="col-span-2 font-semibold">振替対象外</dt>
        <dd className="col-span-10">{is_excluded_transfer ? 'はい' : 'いいえ'}</dd>
      </dl>
    </section>
  );
};

export default ClinicBasicInfo;
